import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useMutation, useReadQuery } from '@apollo/client/react'
import { ErrorAlert } from '@repo/shared-client/components/blocks'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import PlaceForm from '@web/src/features/place/place-form/place-form'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useState } from 'react'
import toast from 'react-hot-toast'
import { PlaceInfo } from 'src/lib/fragments'
import type {
  EditPlaceByIdQuery,
  EditPlaceByIdQueryVariables,
  UpdatePlaceInput,
  UpdatePlaceMutation,
  UpdatePlaceMutationVariables,
} from 'types/graphql'

const EDIT_PLACE_QUERY: TypedDocumentNode<EditPlaceByIdQuery, EditPlaceByIdQueryVariables> = gql`
  query EditPlaceById($placeIdOrSlug: String!) {
    place: place(placeIdOrSlug: $placeIdOrSlug) {
      ...PlaceInfo
    }
    placeTags {
      id
      name
    }
  }
  ${PlaceInfo}
`

const UPDATE_PLACE_MUTATION: TypedDocumentNode<UpdatePlaceMutation, UpdatePlaceMutationVariables> = gql`
  mutation UpdatePlaceMutation($placeIdOrSlug: String!, $input: UpdatePlaceInput!) {
    updatePlace(placeIdOrSlug: $placeIdOrSlug, input: $input) {
      ...PlaceInfo
    }
  }
  ${PlaceInfo}
`

export const Route = createFileRoute('/(auth)/(main-layout)/place/$placeIdOrSlug_/edit')({
  component: EditPlacePage,
  head: () =>
    seo({
      title: `Edit Place | ${env.VITE_APP_NAME}`,
      description: 'Edit a Place entry',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, EDIT_PLACE_QUERY, {
      variables: { placeIdOrSlug: params.placeIdOrSlug },
    })
    return { queryRef }
  },
})

function EditPlacePage() {
  const { t } = useTranslation()
  const routeParams = Route.useParams()
  const placeIdOrSlug = routeParams.placeIdOrSlug
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<EditPlaceByIdQuery>)
  const isInitialLoading = networkStatus === NetworkStatus.loading

  const [updatePlace] = useMutation(UPDATE_PLACE_MUTATION, {
    onCompleted: (result) => {
      toast.success(t('Place updated'))
      navigate({
        to: '/place/$placeIdOrSlug',
        params: { placeIdOrSlug: result.updatePlace.slug ?? result.updatePlace.id } as never,
      })
      setShowSpinnerValue(false)
    },
    onError: (mutationError) => {
      toast.error(mutationError.message)
      setShowSpinnerValue(false)
    },
  })

  const onSave = (input: UpdatePlaceInput) => {
    const segment = data?.place != null ? (data.place.slug ?? data.place.id) : placeIdOrSlug
    setShowSpinnerValue(true)
    updatePlace({
      variables: { placeIdOrSlug: segment, input },
    })
  }

  if (isInitialLoading) {
    return <SFPageLoading />
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  if (!(data?.place && data?.placeTags)) {
    return <ErrorAlert error={new Error(t('Place or place tags not found'))} />
  }

  return (
    <div className="mx-auto w-full max-w-2xl pt-4">
      <div className="flex w-full flex-col items-stretch gap-4">
        <h1 className="w-full text-center font-bold text-4xl">{t('Edit Place')}</h1>
        <PlaceForm onSave={onSave} place={data.place} placeTags={data.placeTags} showSpinner={showSpinner} />
      </div>
    </div>
  )
}
